import { useState } from "react";
import { toast } from "sonner";
import { FileDown, Eye } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FILE_NAME = "Catalogo-Brangus-Hacienda-FV.pdf";
const getLead = () => { try { return JSON.parse(localStorage.getItem("fv_lead")); } catch { return null; } };
const sendLead = (lead, action) =>
  api.post("/catalog/lead", { ...lead, action, visitor_id: localStorage.getItem("fv_vid") || "" });

const es = (msg) => ({
  onInvalid: (e) => e.target.setCustomValidity(e.target.validity.typeMismatch ? "Ingrese un email válido" : msg),
  onInput: (e) => e.target.setCustomValidity(""),
});

const PdfLink = ({ url, action, onClick, testid, className, children }) => (
  <a href={url} {...(action === "download" ? { download: FILE_NAME } : { target: "_blank", rel: "noreferrer" })}
    onClick={onClick} data-testid={testid} className={className}>{children}</a>
);

function LeadForm({ action, onDone }) {
  const [f, setF] = useState(getLead() || { name: "", whatsapp: "", email: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await sendLead(f, action);
      localStorage.setItem("fv_lead", JSON.stringify(f));
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="catalog-lead-form">
      <div className="space-y-2"><Label htmlFor="lead-name">Nombre y apellido *</Label>
        <Input id="lead-name" required minLength={2} value={f.name} onChange={set("name")} {...es("Ingrese su nombre")} className="h-11" data-testid="catalog-lead-name-input" /></div>
      <div className="space-y-2"><Label htmlFor="lead-wa">WhatsApp (con código de país) *</Label>
        <Input id="lead-wa" type="tel" required minLength={6} placeholder="+506 8888 8888" value={f.whatsapp} onChange={set("whatsapp")} {...es("Ingrese su número de WhatsApp")} className="h-11" data-testid="catalog-lead-whatsapp-input" /></div>
      <div className="space-y-2"><Label htmlFor="lead-email">Email (opcional, le enviamos el catálogo)</Label>
        <Input id="lead-email" type="email" value={f.email} onChange={set("email")} {...es("Ingrese un email válido")} className="h-11" data-testid="catalog-lead-email-input" /></div>
      <button disabled={busy} className="btn-fv btn-red w-full justify-center disabled:opacity-60" data-testid="catalog-lead-submit-button">
        {busy ? "Enviando…" : "Continuar"}
      </button>
    </form>
  );
}

export const CatalogAccess = ({ pdfUrl }) => {
  const url = fileUrl(pdfUrl);
  const [action, setAction] = useState(null);
  const [ready, setReady] = useState(false);
  const [known, setKnown] = useState(!!getLead());

  const click = (a) => (e) => {
    const lead = getLead();
    if (lead) return void sendLead(lead, a).catch(() => {});
    e.preventDefault();
    setReady(false);
    setAction(a);
  };
  const btn = { download: ["btn-fv btn-red", FileDown, "Descargar catálogo"], view: ["btn-fv btn-outline", Eye, "Ver online"] };

  return (
    <>
      {["download", "view"].map((a) => {
        const [cls, Icon, label] = btn[a];
        return (
          <PdfLink key={a} url={url} action={known ? a : "view"} onClick={click(a)} className={cls}
            testid={a === "download" ? "catalogo-download-pdf-button" : "catalogo-view-pdf-button"}>
            <Icon size={18} />{label}
          </PdfLink>
        );
      })}
      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="max-w-md bg-[#F8F5EE]" data-testid="catalog-lead-dialog">
          <DialogTitle className="f-display text-2xl">{ready ? "¡Listo! Ya puede verlo" : "Antes de continuar"}</DialogTitle>
          <DialogDescription>
            {ready ? "Gracias por su interés. Lo contactaremos para asesorarlo." : "Déjenos sus datos para poder asesorarlo sobre la genética, embriones y semen del catálogo."}
          </DialogDescription>
          {ready ? (
            <PdfLink url={url} action={action} onClick={() => setAction(null)} testid="catalog-lead-open-button" className="btn-fv btn-red w-full justify-center">
              {action === "download" ? <><FileDown size={18} />Descargar ahora</> : <><Eye size={18} />Abrir catálogo</>}
            </PdfLink>
          ) : (
            <LeadForm action={action} onDone={() => { setReady(true); setKnown(true); }} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
